import React from "react";

/**
 * Shared Case Analysis body — same sections as /case-analysis/:id
 * (CaseAnalysisNextSteps). Used on that page and as a Copilot chat bubble.
 */

export const LikelihoodBadge = ({ value }) => {
  const v = (value || "").toLowerCase();
  const cls = v.includes("high")
    ? "bg-red-100 text-red-700"
    : v.includes("low")
      ? "bg-gray-200 text-gray-600"
      : "bg-amber-100 text-amber-700";
  return (
    <span
      className={`inline-flex flex-shrink-0 items-center whitespace-nowrap text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${cls}`}
    >
      {value || "Medium"}
    </span>
  );
};

export const Section = ({ title, children }) => (
  <div>
    <h3 className="text-sm font-bold text-[#253439] mb-2 flex items-center gap-2">
      <span className="w-1.5 h-1.5 rounded-full bg-[#06b6d4]" />
      {title}
    </h3>
    {children}
  </div>
);

/**
 * @param {object} props
 * @param {object} props.analysis — CaseAnalysisResponse-shaped payload
 * @param {boolean} [props.showDiscussion=false]
 * @param {React.ReactNode} [props.discussion] — optional Discussion section body
 */
export default function CaseAnalysisPanels({
  analysis,
  showDiscussion = false,
  discussion = null,
}) {
  if (!analysis) return null;

  const caseMeta = analysis.case || {};
  const caseLabel = caseMeta.caseNumber || analysis.case_id || "";
  const caseUrl = caseMeta.url || "";

  return (
    <div className="space-y-5 ca-panels">
      {(caseLabel || caseMeta.product || caseMeta.productType) && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
          {caseLabel && (
            caseUrl ? (
              <a
                href={caseUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-cyan-700 font-semibold underline decoration-cyan-300 hover:decoration-cyan-700"
              >
                Case {caseLabel}
              </a>
            ) : (
              <span className="font-mono text-cyan-700 font-semibold">
                Case {caseLabel}
              </span>
            )
          )}
          {(caseMeta.product || caseMeta.productType) && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
              {caseMeta.product || caseMeta.productType}
            </span>
          )}
          {caseMeta.status && (
            <span className="text-[10px] text-gray-400">{caseMeta.status}</span>
          )}
          {caseMeta.priority && (
            <span className="text-[10px] text-gray-400">{caseMeta.priority}</span>
          )}
        </div>
      )}

      <Section title="Case Analysis (so far)">
        <p className="text-sm text-[#253439] leading-relaxed whitespace-pre-wrap">
          {analysis.case_analysis || "No analysis available."}
        </p>
      </Section>

      {analysis.root_cause_identified && analysis.root_cause ? (
        <Section title="Root Cause (identified)">
          <div className="px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
            {analysis.root_cause}
          </div>
        </Section>
      ) : (
        <Section title="Potential Causes — with Diagnostic Steps">
          {(analysis.potential_causes || []).length === 0 ? (
            <p className="text-sm text-gray-400">No potential causes generated.</p>
          ) : (
            <ol className="space-y-3">
              {(analysis.potential_causes || []).map((pc, i) => (
                <li
                  key={i}
                  className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm"
                >
                  <div className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#06b6d4] text-white text-xs font-bold flex items-center justify-center mt-0.5">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm font-semibold text-[#253439] min-w-0">
                          {pc.cause}
                        </span>
                        <LikelihoodBadge value={pc.likelihood} />
                      </div>
                      {(pc.diagnostic_steps || []).length > 0 && (
                        <ul className="mt-2 ml-1 space-y-1">
                          {pc.diagnostic_steps.map((s, si) => (
                            <li
                              key={si}
                              className="text-xs text-gray-600 flex gap-1.5"
                            >
                              <span className="text-[#06b6d4] font-bold">
                                {si + 1}.
                              </span>
                              <span>{s}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Section>
      )}

      <Section title="Similar Cases">
        {(analysis.similar_cases || []).length === 0 ? (
          <p className="text-sm text-gray-400">
            No similar cases found (or case store unavailable).
          </p>
        ) : (
          <ul className="space-y-2">
            {(analysis.similar_cases || []).map((sc, i) => (
              <li
                key={i}
                className="bg-white border border-gray-200 rounded-lg p-3 text-sm"
              >
                <div className="flex items-center gap-2 mb-0.5">
                  {sc.url ? (
                    <a
                      href={sc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-cyan-700 font-semibold underline decoration-cyan-300 hover:decoration-cyan-700"
                    >
                      {sc.case_number || sc.case_id}
                    </a>
                  ) : (
                    <span className="font-mono text-cyan-700 font-semibold">
                      {sc.case_number || sc.case_id}
                    </span>
                  )}
                  {sc.product_type && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                      {sc.product_type}
                    </span>
                  )}
                  {sc.score > 0 && (
                    <span className="text-[10px] text-gray-400">
                      match {(sc.score * 100).toFixed(0)}%
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-600">{sc.summary}</p>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Similar Bugs">
        {(analysis.similar_bugs || []).length === 0 ? (
          <p className="text-sm text-gray-400">
            No similar bugs found (or bug store unavailable).
          </p>
        ) : (
          <ul className="space-y-2">
            {(analysis.similar_bugs || []).map((b, i) => (
              <li
                key={i}
                className="bg-white border border-gray-200 rounded-lg p-3 text-sm"
              >
                <div className="flex items-center gap-2 mb-0.5">
                  {b.url ? (
                    <a
                      href={b.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-purple-700 font-semibold underline decoration-purple-300 hover:decoration-purple-700"
                    >
                      {b.bug_id}
                    </a>
                  ) : (
                    <span className="font-mono text-purple-700 font-semibold">
                      {b.bug_id}
                    </span>
                  )}
                  {b.status && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                      {b.status}
                    </span>
                  )}
                  {b.priority && (
                    <span className="text-[10px] text-gray-400">{b.priority}</span>
                  )}
                  {b.score > 0 && (
                    <span className="text-[10px] text-gray-400">
                      match {(b.score * 100).toFixed(0)}%
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-600">{b.summary}</p>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {!analysis.root_cause_identified &&
        (analysis.clarifying_questions || []).length > 0 && (
          <Section title="Next-Step Clarifying Questions">
            <ul className="space-y-1.5">
              {analysis.clarifying_questions.map((q, i) => (
                <li key={i} className="text-sm text-[#253439] flex gap-2">
                  <span className="text-[#06b6d4] font-bold">{i + 1}.</span>
                  <span>{q}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

      {(analysis.warnings || []).length > 0 && (
        <div className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          {analysis.warnings.join(" · ")}
        </div>
      )}

      {showDiscussion && discussion}
    </div>
  );
}
